import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Sparkles, FileText, Loader2, Download, BarChart3, FileSpreadsheet, Presentation, Check, ClipboardList } from 'lucide-react';

const AIReportDialog = ({ open, onOpenChange, projects = [] }) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [users, setUsers] = useState([]);
  const [exportFormat, setExportFormat] = useState('html');
  const [exporting, setExporting] = useState(false);
  
  const [settings, setSettings] = useState({
    focus_areas: ['status', 'risks', 'recommendations'],
    selected_projects: [],
    filter_owner: 'all',
    filter_status: 'all',
    detail_level: 'standard',
    language: 'de',
    include_charts: true,
    detailed_per_project: false
  });

  useEffect(() => {
    if (open) {
      fetchUsers();
      setSettings(prev => ({ ...prev, selected_projects: [] }));
      setReport(null);
      setExportFormat('html');
    }
  }, [open]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  };

  const focusOptions = [
    { id: 'status', label: 'Status & Fortschritt', description: 'Aktueller Stand aller Projekte' },
    { id: 'budget', label: 'Budget-Analyse', description: 'Kostenübersicht und Budgetnutzung' },
    { id: 'timeline', label: 'Zeitplan', description: 'Termine und Meilensteine' },
    { id: 'risks', label: 'Risiken', description: 'Überfällige und kritische Tasks' },
    { id: 'recommendations', label: 'Empfehlungen', description: 'Handlungsempfehlungen' }
  ];

  const toggleFocusArea = (id) => {
    setSettings(prev => ({
      ...prev,
      focus_areas: prev.focus_areas.includes(id)
        ? prev.focus_areas.filter(f => f !== id)
        : [...prev.focus_areas, id]
    }));
  };

  const toggleProject = (projectId) => {
    setSettings(prev => ({
      ...prev,
      selected_projects: prev.selected_projects.includes(projectId)
        ? prev.selected_projects.filter(p => p !== projectId)
        : [...prev.selected_projects, projectId]
    }));
  };

  const selectAllProjects = () => {
    setSettings(prev => ({ ...prev, selected_projects: [] }));
  };

  const filteredProjects = projects.filter(p => {
    if (settings.filter_owner !== 'all' && p.owner_id !== settings.filter_owner) return false;
    if (settings.filter_status !== 'all' && p.status !== settings.filter_status) return false;
    return true;
  });

  const projectsForReport = settings.selected_projects.length > 0
    ? filteredProjects.filter(p => settings.selected_projects.includes(p.id))
    : filteredProjects;

  const handleGenerate = async () => {
    if (settings.focus_areas.length === 0) {
      toast.error('Bitte mindestens einen Fokusbereich auswählen');
      return;
    }

    if (projectsForReport.length === 0) {
      toast.error('Keine Projekte für den Report ausgewählt');
      return;
    }

    setLoading(true);
    setReport(null);

    try {
      const response = await axios.post(`${API}/dashboard/ai-report`, {
        focus_areas: settings.focus_areas,
        include_projects: projectsForReport.map(p => p.id),
        detail_level: settings.detail_level,
        language: settings.language,
        include_charts: settings.include_charts,
        detailed_per_project: settings.detailed_per_project
      });

      setReport(response.data);
      toast.success('Report erfolgreich generiert');
    } catch (error) {
      console.error('Report generation error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Generieren des Reports');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!report) return;
    
    setExporting(true);
    
    try {
      if (exportFormat === 'html') {
        downloadHTML();
      } else if (exportFormat === 'pdf') {
        await downloadPDF();
      } else if (exportFormat === 'pptx') {
        await downloadPPTX();
      }
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  const downloadHTML = () => {
    const htmlContent = `<!DOCTYPE html>
<html lang="de">
<head>
  <meta charset="UTF-8">
  <title>Projekt-Statusbericht</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 900px; margin: 40px auto; padding: 20px; line-height: 1.6; color: #1a1a1a; }
    h1 { color: #1a1a1a; border-bottom: 3px solid #8b5cf6; padding-bottom: 15px; font-size: 28px; }
    h2 { color: #374151; margin-top: 35px; font-size: 22px; border-left: 4px solid #8b5cf6; padding-left: 12px; }
    h3 { color: #4b5563; font-size: 18px; }
    ul { padding-left: 20px; }
    li { margin: 10px 0; }
    .text-green-600 { color: #16a34a; font-weight: 500; }
    .text-red-600 { color: #dc2626; font-weight: 500; }
    .text-yellow-600 { color: #ca8a04; font-weight: 500; }
    .summary-box { background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%); border-left: 4px solid #8b5cf6; padding: 20px; margin-top: 30px; border-radius: 8px; }
    .meta { color: #6b7280; font-size: 14px; margin-bottom: 30px; padding: 10px; background: #f9fafb; border-radius: 6px; }
    .kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 15px; margin: 20px 0; }
    .kpi-card { background: #f9fafb; padding: 15px; border-radius: 8px; text-align: center; border: 1px solid #e5e7eb; }
    .kpi-value { font-size: 28px; font-weight: bold; color: #8b5cf6; }
    .kpi-label { font-size: 12px; color: #6b7280; margin-top: 5px; }
    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
    th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e5e7eb; }
    th { background: #f9fafb; font-weight: 600; }
    .project-card { background: #fafafa; border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px; margin: 15px 0; }
    .project-card h3 { margin-top: 0; color: #8b5cf6; }
  </style>
</head>
<body>
  <h1>📊 Projekt-Statusbericht</h1>
  <div class="meta">
    <strong>Generiert am:</strong> ${new Date(report.generated_at).toLocaleString('de-CH')} | 
    <strong>Projekte:</strong> ${projectsForReport.length}
  </div>
  ${report.report_html}
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `statusbericht_${new Date().toISOString().split('T')[0]}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('HTML heruntergeladen');
  };

  const downloadPDF = async () => {
    try {
      // Prepare project data for chart generation
      const projectData = projectsForReport.map(p => ({
        name: p.name,
        progress: `${p.completed_tasks || 0}/${p.task_count || 0} (${p.task_count > 0 ? Math.round((p.completed_tasks || 0) / p.task_count * 100) : 0}%)`,
        status: p.status,
        budget: `CHF ${p.actual_cost || 0} / ${p.planned_cost || 0}`
      }));

      const response = await axios.post(`${API}/dashboard/ai-report/export-pdf`, {
        report_html: report.report_html,
        generated_at: report.generated_at,
        include_charts: settings.include_charts,
        project_data: projectData
      }, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `statusbericht_${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF heruntergeladen');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('PDF-Export fehlgeschlagen');
    }
  };

  const downloadPPTX = async () => {
    try {
      const response = await axios.post(`${API}/dashboard/ai-report/export-pptx`, {
        report_html: report.report_html,
        generated_at: report.generated_at,
        include_charts: settings.include_charts,
        projects: projectsForReport
      }, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `statusbericht_${new Date().toISOString().split('T')[0]}.pptx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PowerPoint heruntergeladen');
    } catch (error) {
      console.error('PPTX export error:', error);
      toast.error('PowerPoint-Export fehlgeschlagen');
    }
  };

  const handleClose = () => {
    setReport(null);
    onOpenChange(false);
  };

  const projectOwners = [...new Set(projects.map(p => p.owner_id))];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500" />
            KI-Statusbericht generieren
          </DialogTitle>
          <DialogDescription>
            Lassen Sie einen intelligenten Bericht über Ihre Projekte erstellen
          </DialogDescription>
        </DialogHeader>

        {!report ? (
          <div className="space-y-4 py-4 overflow-y-auto">
            {/* Focus Areas */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Fokus-Bereiche</Label>
              <div className="grid grid-cols-2 gap-2">
                {focusOptions.map((option) => (
                  <div
                    key={option.id}
                    className={`p-2.5 rounded-lg border cursor-pointer transition-all ${
                      settings.focus_areas.includes(option.id)
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30'
                        : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                    }`}
                    onClick={() => toggleFocusArea(option.id)}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox checked={settings.focus_areas.includes(option.id)} onChange={() => {}} />
                      <span className="font-medium text-sm">{option.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Project Filters */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Projektleiter</Label>
                <Select
                  value={settings.filter_owner}
                  onValueChange={(value) => setSettings(prev => ({ ...prev, filter_owner: value, selected_projects: [] }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Projektleiter</SelectItem>
                    {users.filter(u => projectOwners.includes(u.id)).map((user) => (
                      <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Status</Label>
                <Select
                  value={settings.filter_status}
                  onValueChange={(value) => setSettings(prev => ({ ...prev, filter_status: value, selected_projects: [] }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Status</SelectItem>
                    <SelectItem value="active">Aktiv</SelectItem>
                    <SelectItem value="completed">Abgeschlossen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Project Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Projekte ({projectsForReport.length}/{filteredProjects.length})</Label>
                <Button variant="ghost" size="sm" onClick={selectAllProjects} className="text-xs h-7">
                  Alle auswählen
                </Button>
              </div>
              <div className="border rounded-lg p-2 max-h-32 overflow-y-auto space-y-1">
                {filteredProjects.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">Keine Projekte</p>
                ) : (
                  filteredProjects.map((project) => {
                    const isSelected = settings.selected_projects.length === 0 || settings.selected_projects.includes(project.id);
                    return (
                      <div
                        key={project.id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          isSelected ? 'bg-purple-50 dark:bg-purple-950/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                        onClick={() => toggleProject(project.id)}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                          isSelected ? 'bg-purple-500 border-purple-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="text-sm truncate flex-1">{project.name}</span>
                        <span className="text-xs text-muted-foreground">{project.task_count || 0} Tasks</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Settings Row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Detailgrad</Label>
                <Select
                  value={settings.detail_level}
                  onValueChange={(value) => setSettings(prev => ({ ...prev, detail_level: value }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brief">Kurz</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="detailed">Ausführlich</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Sprache</Label>
                <Select
                  value={settings.language}
                  onValueChange={(value) => setSettings(prev => ({ ...prev, language: value }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="en">Englisch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Checkboxes */}
            <div className="space-y-2">
              <div 
                className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                  settings.include_charts ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30' : 'border-gray-200 dark:border-gray-700'
                }`}
                onClick={() => setSettings(prev => ({ ...prev, include_charts: !prev.include_charts }))}
              >
                <Checkbox checked={settings.include_charts} onChange={() => {}} />
                <BarChart3 className="w-4 h-4 text-purple-500" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Grafiken & KPIs einbinden</p>
                </div>
              </div>
              
              <div 
                className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                  settings.detailed_per_project ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30' : 'border-gray-200 dark:border-gray-700'
                }`}
                onClick={() => setSettings(prev => ({ ...prev, detailed_per_project: !prev.detailed_per_project }))}
              >
                <Checkbox checked={settings.detailed_per_project} onChange={() => {}} />
                <ClipboardList className="w-4 h-4 text-purple-500" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Detaillierte Projektbewertung</p>
                  <p className="text-xs text-muted-foreground">Jedes Projekt einzeln analysieren</p>
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={loading || settings.focus_areas.length === 0 || projectsForReport.length === 0}
              className="w-full bg-purple-600 hover:bg-purple-700"
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Report wird generiert...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Report generieren
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Report Header with Export */}
            <div className="flex items-center justify-between py-3 border-b gap-4">
              <div className="text-sm text-muted-foreground">
                Generiert am {new Date(report.generated_at).toLocaleString('de-CH')}
              </div>
              <div className="flex items-center gap-2">
                <Select value={exportFormat} onValueChange={setExportFormat}>
                  <SelectTrigger className="w-32 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="html">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        HTML
                      </div>
                    </SelectItem>
                    <SelectItem value="pdf">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4" />
                        PDF
                      </div>
                    </SelectItem>
                    <SelectItem value="pptx">
                      <div className="flex items-center gap-2">
                        <Presentation className="w-4 h-4" />
                        PowerPoint
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={handleExport} disabled={exporting}>
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                  Export
                </Button>
                <Button variant="outline" size="sm" onClick={() => setReport(null)}>
                  Neu
                </Button>
              </div>
            </div>

            {/* Report Content */}
            <div 
              className="flex-1 overflow-y-auto py-4 prose prose-sm max-w-none dark:prose-invert
                [&_.summary-box]:bg-purple-50 [&_.summary-box]:dark:bg-purple-950/30 
                [&_.summary-box]:border-l-4 [&_.summary-box]:border-purple-500 
                [&_.summary-box]:p-4 [&_.summary-box]:rounded-r-lg [&_.summary-box]:mt-6
                [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-3 [&_h2]:text-purple-700 [&_h2]:dark:text-purple-400
                [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2
                [&_ul]:my-2 [&_li]:my-1
                [&_.text-green-600]:text-green-600 [&_.text-green-600]:dark:text-green-400 [&_.text-green-600]:font-medium
                [&_.text-red-600]:text-red-600 [&_.text-red-600]:dark:text-red-400 [&_.text-red-600]:font-medium
                [&_.text-yellow-600]:text-yellow-600 [&_.text-yellow-600]:dark:text-yellow-400 [&_.text-yellow-600]:font-medium
                [&_.kpi-grid]:grid [&_.kpi-grid]:grid-cols-3 [&_.kpi-grid]:gap-3 [&_.kpi-grid]:my-4
                [&_.kpi-card]:bg-gray-50 [&_.kpi-card]:dark:bg-gray-800 [&_.kpi-card]:p-3 [&_.kpi-card]:rounded-lg [&_.kpi-card]:text-center [&_.kpi-card]:border
                [&_.kpi-value]:text-2xl [&_.kpi-value]:font-bold [&_.kpi-value]:text-purple-600
                [&_.kpi-label]:text-xs [&_.kpi-label]:text-muted-foreground
                [&_.project-card]:bg-gray-50 [&_.project-card]:dark:bg-gray-800 [&_.project-card]:p-4 [&_.project-card]:rounded-lg [&_.project-card]:my-4 [&_.project-card]:border
                [&_table]:w-full [&_table]:my-4 [&_th]:bg-gray-50 [&_th]:dark:bg-gray-800 [&_th]:p-2 [&_th]:text-left [&_th]:text-sm [&_td]:p-2 [&_td]:border-b [&_td]:text-sm"
              dangerouslySetInnerHTML={{ __html: report.report_html }}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AIReportDialog;
